import { Icon, IconButton, Stack, Title, useTheme } from "@introist/react-foundation/v2";
import styled from "styled-components";
import { Avatar } from "../../../../components/molecules";
import { OptionalAttendeeButton } from "./OptionalAttendeeButton";
import { isEmail } from "../../../../utils/string-templater";
import { TagSelector } from "../../../system/tags/TagSelector";
import { TagPathTitle } from "../../../system/tags/TagPathTitle";

const StyledAttendeeCard = styled.div<{}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-height: 36px;
  box-sizing: border-box;
  padding: 4px var(--spacing-small);
  border: 1px solid transparent;
  transition: border-color 160ms ease;
  border-radius: var(--rounding-medium);

  :hover {
    border-color: var(--palette-border-subdued);
    cursor: pointer;

    .remove-btn {
      opacity: 1;
    }
  }
`;

const AvatarBackground = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: var(--rounding-small);
  background: var(--palette-primary-ghosted);
`;

const RemoveButton = styled(IconButton)`
  opacity: 0;
`;

type Attendee = { value: string; optional?: boolean };

type MeetingAttendeeSelectProps = {
  value: Attendee[];
  onChange: (attendees: Attendee[]) => void;
  workflowId?: string;
};

export const MeetingAttendeeSelect = ({ value, onChange, workflowId }: MeetingAttendeeSelectProps) => {
  const { theme } = useTheme();

  return (
    <Stack vertical gap="xSmall">
      {value.map(attendee => {
        return (
          <StyledAttendeeCard key={attendee.value}>
            <Stack>
              <AvatarBackground>
                {isEmail(attendee.value) ? (
                  <Avatar nameOrEmail={attendee.value} />
                ) : (
                  <Icon
                    style={{
                      width: 14
                    }}
                    name="attribute"
                    color={theme.palette.purple.default}
                  />
                )}
              </AvatarBackground>
              <Title variant="bold">
                {isEmail(attendee.value) ? (
                  attendee.value
                ) : (
                  <TagPathTitle path={attendee.value} workflowId={workflowId} />
                )}
              </Title>
            </Stack>
            <Stack>
              <OptionalAttendeeButton
                optional={attendee.optional}
                onClick={() =>
                  onChange(
                    value.map(a => {
                      if (a.value === attendee.value) {
                        return { ...a, optional: !a.optional };
                      }
                      return a;
                    })
                  )
                }
              />
              <RemoveButton
                className="remove-btn"
                icon="crossSmall"
                onClick={() => onChange(value.filter(a => a.value !== attendee.value))}
              />
            </Stack>
          </StyledAttendeeCard>
        );
      })}
      <TagSelector
        variant="blended"
        size="small"
        title="Add attendees"
        onSelect={tag => onChange([...value, { value: tag.tag }])}
        ctx={{
          workflowId,
          fieldTypes: ["email", "employee"]
        }}
        freeText={{
          validate: value => !!isEmail(value),
          onSelect: email => onChange([...value, { value: email }])
        }}
      />
    </Stack>
  );
};
